import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FlagIcon from '@mui/icons-material/Flag';
import AidStationItem from './AidStationItem';
import DropBagItemsDialog from './DropBagItemsDialog';
import { buildCutoffElapsedByIndex } from '../../utils/formatters';

const AidStationsSection = ({ 
  aidStations,
  onAddAidStation, 
  onAddFinish, 
  onRemoveAidStation, 
  onAidStationChange,
  onAddItemToDropBag,
  onAddItemsToDropBag,
  onRemoveItemFromDropBag,
  onRemoveItemsFromDropBag,
  errors = {},
  lastAidStationRef,
  unit = 'miles',  // Default to miles if not provided
  distance = 0,    // Default to 0 if not provided
  startTimeHours = null,
  startTimeMinutes = null,
  startTimeAMPM = null
}) => {
  const startTimeInfo = useMemo(() => {
    if (startTimeHours == null && startTimeMinutes == null && startTimeAMPM == null) {
      return null;
    }
    return { startTimeHours, startTimeMinutes, startTimeAMPM };
  }, [startTimeHours, startTimeMinutes, startTimeAMPM]);

  // Compute monotonic elapsed-from-start cutoff hours for each aid station so the live
  // hint and downstream display can handle multi-day races correctly.
  const cutoffElapsedByIndex = useMemo(
    () => buildCutoffElapsedByIndex(aidStations, startTimeInfo),
    [aidStations, startTimeInfo]
  );
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [currentDropBagIndex, setCurrentDropBagIndex] = useState(null);
  
  // Use the unit prop directly
  const aidStationUnit = unit;
  
  // Open the drop bag items dialog for a specific aid station
  const handleOpenItemsDialog = (index) => {
    setCurrentDropBagIndex(index);
    setOpenItemDialog(true);
  };
  
  // Close the drop bag items dialog
  const handleCloseItemsDialog = () => {
    setOpenItemDialog(false);
    setCurrentDropBagIndex(null);
  };
  
  // Add multiple items at once to a drop bag
  const handleAddMultipleItems = (items) => {
    if (!items || items.length === 0) return;
    
    console.log(`[DEBUG] AidStationsSection adding ${items.length} items to drop bag ${currentDropBagIndex}`);
    onAddItemsToDropBag(currentDropBagIndex, items);
  };
  
  // Add a single item to the current drop bag (legacy support)
  const handleAddItem = (item) => {
    if (item.trim() === '') return;
    
    console.log(`[DEBUG] AidStationItem adding single item to dropbag ${currentDropBagIndex}: "${item}"`);
    onAddItemToDropBag(currentDropBagIndex, item);
  };
  
  // Remove an item from a drop bag
  const handleRemoveItem = (itemIndex) => {
    onRemoveItemFromDropBag(currentDropBagIndex, itemIndex);
  };

  // Handle direct radio button changes
  const handleMilesClick = () => {
    console.log("Miles radio clicked, explicitly setting value to 'miles'");
    onAidStationChange('aidStationUnit', 'miles', null);
  };

  const handleKilometersClick = () => {
    console.log("Kilometers radio clicked, explicitly setting value to 'kilometers'");
    onAidStationChange('aidStationUnit', 'kilometers', null);
  };

  // Add regular aid station
  const handleAddAidStation = () => {
    onAddAidStation({
      location: '',
      hasDropBag: false,
      hasPacer: false,
      hasCrew: false,
      hasLocation: false,
      items: [],
      aidStopDurationHours: '',
      aidStopDurationMinutes: '',
      isFinish: false
    });
  };

  // Add finish location
  const handleAddFinish = () => {
    // Get the race distance and ensure it's a number
    const raceDistance = parseFloat(distance) || 0;
    
    onAddAidStation({
      location: raceDistance,
      isFinish: true,
      hasLocation: false,
      locationAddress: '',
      name: 'Finish',
      notes: '',
      hasCrew: false,
      // Store original unit information for conversion if needed
      originalLocation: raceDistance,
      originalUnit: unit || 'miles',
      // Disable other options
      hasDropBag: false,
      hasPacer: false,
      items: []
    });
  };

  // Check if finish is already added
  const hasFinish = aidStations.some(station => station.isFinish);

  // Add console log to see what's coming from the formData
  useEffect(() => {
    console.log("AidStationsSection: unit =", unit);
  }, [unit]);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Aid Station Locations Unit</FormLabel>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <FormControlLabel
              value="miles"
              control={
                <Radio
                  checked={aidStationUnit === 'miles'}
                  onChange={handleMilesClick}
                />
              }
              label="Miles"
            />
            <FormControlLabel
              value="kilometers"
              control={
                <Radio
                  checked={aidStationUnit === 'kilometers'}
                  onChange={handleKilometersClick}
                />
              }
              label="Kilometers"
            />
          </Box>
        </FormControl>
      </Box>
      
      {aidStations.map((station, index) => {
        // Find the closest preceding aid station that has its own cutoff elapsed so
        // monotonicity carries through stations without cutoffs.
        let prevElapsed = 0;
        let prevCutoffLocation = 0;
        for (let i = index - 1; i >= 0; i -= 1) {
          if (cutoffElapsedByIndex[i] != null) {
            prevElapsed = cutoffElapsedByIndex[i];
            prevCutoffLocation = parseFloat(aidStations[i].location) || 0;
            break;
          }
        }
        return (
          <AidStationItem
            key={index}
            index={index}
            station={station}
            onRemove={onRemoveAidStation}
            onChange={onAidStationChange}
            onOpenItemsDialog={handleOpenItemsDialog}
            error={errors[`aidStation_${index}`]}
            unit={aidStationUnit}
            startTimeInfo={startTimeInfo}
            prevCutoffElapsedHours={prevElapsed}
            prevCutoffLocation={prevCutoffLocation}
            ref={index === aidStations.length - 1 ? lastAidStationRef : null}
          />
        );
      })}
      
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}
           ref={aidStations.length === 0 ? lastAidStationRef : null}
      >
        <Button 
          variant="outlined" 
          startIcon={<AddIcon />}
          onClick={handleAddAidStation}
        >
          Add Aid Station
        </Button>
        {!hasFinish && (
          <Button 
            variant="outlined" 
            startIcon={<FlagIcon />}
            onClick={handleAddFinish}
            sx={{ 
              borderColor: '#4caf50',
              color: '#4caf50',
              '&:hover': {
                borderColor: '#45a049',
                backgroundColor: 'rgba(76, 175, 80, 0.04)'
              }
            }}
          >
            Add Finish
          </Button>
        )}
      </Box>
      
      {/* Drop Bag Items Dialog */}
      <DropBagItemsDialog 
        open={openItemDialog}
        onClose={handleCloseItemsDialog}
        items={currentDropBagIndex !== null ? aidStations[currentDropBagIndex]?.items || [] : []}
        onAddItem={handleAddItem}
        onAddMultipleItems={handleAddMultipleItems}
        onRemoveItem={handleRemoveItem}
        stationLocation={currentDropBagIndex !== null ? aidStations[currentDropBagIndex]?.location : ''}
        unit={aidStationUnit}
      />
    </Box>
  );
};

export default AidStationsSection; 